import {
  BulkBlockDeal,
  BulkBlockStats,
  DealSide,
  DealType,
  InvestorCategory,
  RawBulkBlockPayload,
} from '../types';
import { getCompanyName } from '../hooks/useCompanyName';

export type {
  BulkBlockDeal,
  BulkBlockStats,
  DealSide,
  DealType,
  InvestorCategory,
  RawBulkBlockPayload,
};

export const BULK_BLOCK_DEALS_URL = 'https://mtf.trading/bulk_block_deals.json';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

let memoryCache: BulkBlockDeal[] | null = null;
let cacheTimestamp = 0;
let pendingFetch: Promise<BulkBlockDeal[]> | null = null;

/**
 * Classifies the transacting entity into recognizable institutional segments.
 */
export const classifyInvestor = (
  clientName: string,
  symbol?: string,
  companyName?: string
): InvestorCategory => {
  const upper = (clientName || '').trim().toUpperCase();
  if (!upper) return 'Corporate / Other';

  // 1. Marquee Indian HNIs and Celebrated Value Investors
  if (
    /ASHISH\s+KACHOLIA|VIJAY\s+KEDIA|REKHA\s+JHUNJHUNWALA|RAKESH\s+JHUNJHUNWALA|RADHAKISHAN\s+DAMANI|MUKUL\s+AGRAWAL|PORINJU\s+VELIYATH|SUNIL\s+SINGHANIA|MADHUSUDAN\s+KELA|NEMISH\s+SHAH|DOLLY\s+KHANNA|RAMESH\s+DAMANI|KENNETH\s+ANDRADE|ANIL\s+KUMAR\s+GOEL|AKASH\s+BHANSHALI|MONISH\s+PABRAI|SAURABH\s+MUKHERJEA|SHIVANI\s+TEJAS/i.test(
      upper
    )
  ) {
    return 'Marquee Investor';
  }

  // 2. Foreign Institutional Investors, Sovereign Wealth Funds, Global Desks (FII)
  if (
    /MORGAN\s+STANLEY|GOLDMAN\s+SACHS|CITIGROUP|SOCIETE\s+GENERALE|BNP\s+PARIBAS|NORGES\s+BANK|GOVERNMENT\s+PENSION\s+FUND|VANGUARD|BLACKROCK|NOMURA|MERRILL\s+LYNCH|UBS|CREDIT\s+SUISSE|BARCLAYS|JPMORGAN|JP\s+MORGAN|MARSHALL\s+WACE|BOFA|FIDELITY|CAPITAL\s+GROUP|TEMPLETON|MACQUARIE|DEUTSCHE\s+BANK|CALPERS|ABU\s+DHABI|GIC\s+PRIVATE|TEMASEK/i.test(
      upper
    )
  ) {
    return 'FII / Global';
  }

  // 3. Domestic Mutual Funds, Insurance Companies, State Funds (DII)
  if (
    /MUTUAL\s+FUND|ASSET\s+MANAGEMENT|TRUSTEE|LIFE\s+INSURANCE|\bLIC\b|GENERAL\s+INSURANCE|\bPENSION\b|PROVIDENT\s+FUND|SBI\s+MF|HDFC\s+MF|ICICI\s+PRUDENTIAL|NIPPON\s+INDIA|KOTAK\s+MAHINDRA|MIRAE\s+ASSET|BANDHAN|UTI|QUANT|MOTILAL|TATA\s+AIG|ADITYA\s+BIRLA|SUNDARAM|CANARA\s+ROBECO|FRANKLIN|EDELWEISS|HSBC|DSP|WHITE\s+OAK|ABAKKUS/i.test(
      upper
    )
  ) {
    return 'Mutual Fund / DII';
  }

  // 4. Proprietary Quant & High-Frequency Trading Desks
  if (
    /QE\s+SECURITIES|HRTI|JUMP\s+TRADING|GRAVITON|ALPHAGREP|NK\s+SECURITIES|MICROCURVES|JUNOMONETA|TOWER\s+RESEARCH|CITADEL|JANE\s+STREET|OPTIVER|FLOW\s+TRADERS|XR\s+TRADING|DRW|EAST\s+BRIDGE|IRAGE|ELIXIR\s+WEALTH/i.test(
      upper
    )
  ) {
    return 'Prop / Quantitative';
  }

  // 5. Explicit Promoter / Founder / Family Trust Keywords
  if (
    /\b(PROMOTER|PROMOTERS|PROMOTOR|PROMOTORS|PROMOTER\s+GROUP|FOUNDER|FOUNDERS|FAMILY\s+TRUST)\b/i.test(
      upper
    )
  ) {
    return 'Promoter / Group';
  }

  // 6. Curated Known Promoter & Corporate Holding Vehicles
  if (
    /\b(SAGILITY\s+B\.?V\.?|TATA\s+SONS|TATA\s+INDUSTRIES|RELIANCE\s+INDUSTRIAL\s+INVESTMENTS|RELIANCE\s+STRATEGIC\s+INVESTMENTS|ADANI\s+(COMMODITIES|PROPERTIES|TRADEX|HOLDINGS|RAIL|PORT|INFRASTRUCTURE)|EMERGING\s+MARKET\s+INVESTMENT\s+DMCC|TWIN\s+STAR\s+HOLDINGS|VEDANTA\s+HOLDINGS|BHARTI\s+TELECOM|JSW\s+(TECHNO\s+PROJECTS|HOLDINGS|INVESTMENTS)|BAJAJ\s+HOLDINGS|GODREJ\s+(INDUSTRIES|BOYCE|SEEDS|HOLDINGS)|TORRENT\s+INVESTMENTS|BIRLA\s+GROUP\s+HOLDINGS|PILANI\s+INVESTMENT|CHOLAMANDALAM\s+FINANCIAL\s+HOLDINGS|MURUGAPPA\s+HOLDINGS|SHRIRAM\s+CAPITAL|SUNDARAM\s+FINANCE\s+HOLDINGS|MAHINDRA\s+HOLDINGS|KIRLOSKAR\s+INDUSTRIES|MACROTECH\s+DEVELOPERS|FORTIS\s+HEALTHCARE\s+HOLDINGS|MAX\s+VENTURES)\b/i.test(
      upper
    )
  ) {
    return 'Promoter / Group';
  }

  // 7. Symbol correlation (e.g. SAGILITY B.V. on SAGILITY)
  if (symbol) {
    const cleanSym = symbol.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleanSym.length >= 3) {
      const symRegex = new RegExp(`(^|[^A-Z0-9])${cleanSym}([^A-Z0-9]|$)`, 'i');
      if (symRegex.test(upper)) {
        return 'Promoter / Group';
      }
    }
  }

  // 8. Company root name correlation (e.g. distinctive corporate identity matching client entity)
  if (companyName) {
    const GENERIC_WORDS = new Set([
      'INDIA',
      'INDIAN',
      'BHARAT',
      'GLOBAL',
      'INTERNATIONAL',
      'FINANCIAL',
      'SERVICES',
      'COMPANY',
      'CORP',
      'CORPORATION',
      'TECH',
      'TECHNOLOGIES',
      'SYSTEMS',
      'HOLDINGS',
      'VENTURES',
      'LIMITED',
      'ENERGY',
      'POWER',
      'PHARMA',
      'STEEL',
      'CHEMICALS',
      'INFRASTRUCTURE',
      'PROJECTS',
      'LTD',
      'PRIVATE',
      'PVT',
      'COMMODITIES',
      'INDUSTRIES',
      'CAPITAL',
      'INVESTMENTS',
    ]);

    const words = companyName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 4 && !GENERIC_WORDS.has(w));

    if (words.length > 0) {
      const primaryRoot = words[0];
      const rootRegex = new RegExp(`(^|[^A-Z0-9])${primaryRoot}([^A-Z0-9]|$)`, 'i');
      if (rootRegex.test(upper)) {
        return 'Promoter / Group';
      }
    }
  }

  return 'Corporate / Other';
};

/**
 * Normalizes, deduplicates, and parses raw payload into structured BulkBlockDeal records.
 */
export const parseBulkBlockDeals = (payload: RawBulkBlockPayload): BulkBlockDeal[] => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const dealsMap = new Map<string, BulkBlockDeal>();

  // 1. Process recent list (contains company name and pre-computed values)
  if (Array.isArray(payload.recent)) {
    for (const r of payload.recent) {
      if (!r.sym || !r.d) continue;
      const key = `${r.d}_${r.sym}_${r.c}_${r.s}_${r.q}_${r.p}_${r.t}`;
      const side: DealSide = r.s === 'B' ? 'B' : 'S';
      const dealType: DealType = r.t === 'block' ? 'block' : 'bulk';
      const q = Number(r.q) || 0;
      const p = Number(r.p) || 0;
      const valueLakhs = r.val != null ? Number(r.val) : Math.round((q * p) / 100000 * 100) / 100;
      const valueCr = Math.round((valueLakhs / 100) * 100) / 100;

      const symUpper = r.sym.toUpperCase();
      const compName = r.name || getCompanyName(r.sym) || symUpper;

      dealsMap.set(key, {
        id: key,
        date: r.d,
        symbol: symUpper,
        companyName: compName,
        clientName: r.c?.trim() || 'Unknown',
        side,
        quantity: q,
        price: p,
        dealType,
        valueCr,
        valueLakhs,
        category: classifyInvestor(r.c || '', symUpper, compName),
      });
    }
  }

  // 2. Process by_symbol dictionary
  if (payload.by_symbol && typeof payload.by_symbol === 'object') {
    for (const [sym, list] of Object.entries(payload.by_symbol)) {
      if (!Array.isArray(list)) continue;
      const normalizedSym = sym.toUpperCase();
      const fallbackName = getCompanyName(normalizedSym) || normalizedSym;

      for (const d of list) {
        if (!d.d) continue;
        const key = `${d.d}_${normalizedSym}_${d.c}_${d.s}_${d.q}_${d.p}_${d.t}`;
        if (!dealsMap.has(key)) {
          const side: DealSide = d.s === 'B' ? 'B' : 'S';
          const dealType: DealType = d.t === 'block' ? 'block' : 'bulk';
          const q = Number(d.q) || 0;
          const p = Number(d.p) || 0;
          const valueLakhs = Math.round(((q * p) / 100000) * 100) / 100;
          const valueCr = Math.round((valueLakhs / 100) * 100) / 100;

          const compName = d.name || fallbackName;

          dealsMap.set(key, {
            id: key,
            date: d.d,
            symbol: normalizedSym,
            companyName: compName,
            clientName: d.c?.trim() || 'Unknown',
            side,
            quantity: q,
            price: p,
            dealType,
            valueCr,
            valueLakhs,
            category: classifyInvestor(d.c || '', normalizedSym, compName),
          });
        }
      }
    }
  }

  // Convert to array and sort by Date desc, then Value desc
  const allDeals = Array.from(dealsMap.values());
  allDeals.sort((a, b) => {
    if (a.date !== b.date) {
      return b.date.localeCompare(a.date);
    }
    return b.valueCr - a.valueCr;
  });

  return allDeals;
};

/**
 * Fetches bulk and block deals from the daily NSE tape endpoint with memory caching.
 */
export const fetchBulkBlockDeals = async (forceRefresh = false): Promise<BulkBlockDeal[]> => {
  const now = Date.now();
  if (!forceRefresh && memoryCache && now - cacheTimestamp < CACHE_TTL_MS) {
    return memoryCache;
  }

  if (pendingFetch) {
    return pendingFetch;
  }

  pendingFetch = (async () => {
    try {
      const response = await fetch(BULK_BLOCK_DEALS_URL, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bulk & block deals: HTTP ${response.status}`);
      }

      const rawJson = (await response.json()) as RawBulkBlockPayload;
      const parsed = parseBulkBlockDeals(rawJson);

      memoryCache = parsed;
      cacheTimestamp = Date.now();
      return parsed;
    } finally {
      pendingFetch = null;
    }
  })();

  return pendingFetch;
};

/**
 * Computes high-level aggregated summary statistics for any filtered slice of deals.
 */
export const computeBulkBlockStats = (deals: BulkBlockDeal[]): BulkBlockStats => {
  let buyValueCr = 0;
  let sellValueCr = 0;
  let bulkCount = 0;
  let blockCount = 0;
  const symbols = new Set<string>();
  const clients = new Set<string>();

  for (const d of deals) {
    if (d.side === 'B') {
      buyValueCr += d.valueCr;
    } else {
      sellValueCr += d.valueCr;
    }

    if (d.dealType === 'block') {
      blockCount++;
    } else {
      bulkCount++;
    }

    symbols.add(d.symbol);
    clients.add(d.clientName);
  }

  const totalValueCr = Math.round((buyValueCr + sellValueCr) * 100) / 100;
  const netFlowCr = Math.round((buyValueCr - sellValueCr) * 100) / 100;

  return {
    totalDeals: deals.length,
    totalValueCr,
    buyValueCr: Math.round(buyValueCr * 100) / 100,
    sellValueCr: Math.round(sellValueCr * 100) / 100,
    netFlowCr,
    bulkCount,
    blockCount,
    uniqueSymbols: symbols.size,
    uniqueClients: clients.size,
  };
};

/**
 * Extracts unique available trading dates sorted in descending order (latest first).
 */
export const getAvailableDates = (deals: BulkBlockDeal[]): string[] => {
  const datesSet = new Set<string>();
  for (const d of deals) {
    if (d.date) datesSet.add(d.date);
  }
  return Array.from(datesSet).sort((a, b) => b.localeCompare(a));
};

export interface StockDealSummary {
  symbol: string;
  companyName: string;
  buyCount: number;
  sellCount: number;
  totalBuyCr: number;
  totalSellCr: number;
  netFlowCr: number;
  totalValueCr: number;
  deals: BulkBlockDeal[];
  topBuyers: string[];
  topSellers: string[];
}

/**
 * Groups deals by stock symbol for aggregated institutional activity analysis.
 */
export const groupDealsBySymbol = (deals: BulkBlockDeal[]): StockDealSummary[] => {
  const map = new Map<string, StockDealSummary>();

  for (const d of deals) {
    let summary = map.get(d.symbol);
    if (!summary) {
      summary = {
        symbol: d.symbol,
        companyName: d.companyName,
        buyCount: 0,
        sellCount: 0,
        totalBuyCr: 0,
        totalSellCr: 0,
        netFlowCr: 0,
        totalValueCr: 0,
        deals: [],
        topBuyers: [],
        topSellers: [],
      };
      map.set(d.symbol, summary);
    }

    summary.deals.push(d);
    if (d.side === 'B') {
      summary.buyCount++;
      summary.totalBuyCr += d.valueCr;
      if (!summary.topBuyers.includes(d.clientName)) {
        summary.topBuyers.push(d.clientName);
      }
    } else {
      summary.sellCount++;
      summary.totalSellCr += d.valueCr;
      if (!summary.topSellers.includes(d.clientName)) {
        summary.topSellers.push(d.clientName);
      }
    }
  }

  const result = Array.from(map.values()).map((s) => ({
    ...s,
    totalBuyCr: Math.round(s.totalBuyCr * 100) / 100,
    totalSellCr: Math.round(s.totalSellCr * 100) / 100,
    netFlowCr: Math.round((s.totalBuyCr - s.totalSellCr) * 100) / 100,
    totalValueCr: Math.round((s.totalBuyCr + s.totalSellCr) * 100) / 100,
  }));

  // Sort by highest total turnover transacted
  result.sort((a, b) => b.totalValueCr - a.totalValueCr);
  return result;
};

/* ── Smart Screener Definitions (ScanX Intelligence) ────────── */
export type SmartScreenerId =
  | 'all'
  | 'fii_dii_mega'
  | 'super_investors'
  | 'mega_blocks'
  | 'pure_accumulation'
  | 'promoters';

export interface SmartScreenerPreset {
  id: SmartScreenerId;
  label: string;
  icon: string;
  badge?: string;
  description: string;
  matches: (deal: BulkBlockDeal) => boolean;
}

export const SMART_SCREENERS: SmartScreenerPreset[] = [
  {
    id: 'all',
    label: 'All Deals',
    icon: '📊',
    description: 'All bulk & block transactions in selected window',
    matches: () => true,
  },
  {
    id: 'fii_dii_mega',
    label: 'FII/DII Mega Buys',
    icon: '🚀',
    badge: '≥₹10Cr',
    description: 'High-conviction buying by Mutual Funds, DIIs, or FIIs of ₹10 Cr+',
    matches: (d) =>
      d.side === 'B' &&
      (d.category === 'Mutual Fund / DII' || d.category === 'FII / Global') &&
      d.valueCr >= 10,
  },
  {
    id: 'super_investors',
    label: 'Super Investors',
    icon: '⭐',
    badge: 'HNIs',
    description: 'Deals by India’s legendary marquee value investors (Kedia, Kacholia, Damani, Agrawal, etc.)',
    matches: (d) => d.category === 'Marquee Investor',
  },
  {
    id: 'mega_blocks',
    label: 'Mega Blocks',
    icon: '🎯',
    badge: '≥₹25Cr',
    description: 'Large block window transactions of ₹25 Cr+',
    matches: (d) => d.dealType === 'block' && d.valueCr >= 25,
  },
  {
    id: 'pure_accumulation',
    label: 'One-Way Buying',
    icon: '🟢',
    badge: 'No Prop',
    description: 'High-conviction buying (≥ ₹5 Cr) excluding high-frequency prop desks',
    matches: (d) =>
      d.side === 'B' &&
      d.valueCr >= 5 &&
      d.category !== 'Prop / Quantitative',
  },
  {
    id: 'promoters',
    label: 'Promoters & Corp',
    icon: '👑',
    badge: 'Stake Shifts',
    description: 'Transactions by promoters, holding entities, or corporate affiliates (≥ ₹5 Cr)',
    matches: (d) =>
      (d.category === 'Promoter / Group' || d.category === 'Corporate / Other') &&
      d.valueCr >= 5,
  },
];

export interface DealSignal {
  type: 'super_investor' | 'mega_block' | 'fii_dii_mega' | 'promoter_stake' | 'pure_buy';
  label: string;
  badgeClass: string;
  title: string;
}

/**
 * Returns special institutional signal tags for a deal to highlight high-alpha transactions.
 */
export const getDealSignals = (deal: BulkBlockDeal): DealSignal[] => {
  const signals: DealSignal[] = [];

  if (deal.category === 'Promoter / Group') {
    signals.push({
      type: 'promoter_stake',
      label: deal.side === 'B' ? '👑 Promoter Buy' : '👑 Promoter Sell',
      badgeClass: 'ns-bulk-deals__signal--promoter',
      title: `Promoter Stake Action: ${deal.clientName} (${deal.side === 'B' ? 'Bought' : 'Sold'} ₹${deal.valueCr} Cr)`,
    });
  }

  if (deal.category === 'Marquee Investor') {
    signals.push({
      type: 'super_investor',
      label: '⭐ Super Investor',
      badgeClass: 'ns-bulk-deals__signal--marquee',
      title: `${deal.clientName} is a recognized marquee investor`,
    });
  }

  if (deal.dealType === 'block' && deal.valueCr >= 50) {
    signals.push({
      type: 'mega_block',
      label: '🎯 Mega Block',
      badgeClass: 'ns-bulk-deals__signal--block',
      title: `Block transaction exceeding ₹50 Cr (₹${deal.valueCr} Cr)`,
    });
  }

  if (
    deal.side === 'B' &&
    (deal.category === 'Mutual Fund / DII' || deal.category === 'FII / Global') &&
    deal.valueCr >= 25
  ) {
    signals.push({
      type: 'fii_dii_mega',
      label: '🚀 Whale Buy',
      badgeClass: 'ns-bulk-deals__signal--whale',
      title: `High conviction ${deal.category} institutional buy of ₹${deal.valueCr} Cr`,
    });
  }

  return signals;
};

export interface StockDealBadgeInfo {
  type: 'super_investor' | 'mega_block' | 'fii_dii_mega' | 'promoter' | 'bulk_deal';
  icon: string;
  label: string;
  shortLabel: string;
  badgeClass: string;
  title: string;
  dealDate: string;
  valueCr: number;
}

/**
 * Evaluates the deals of a stock and returns the highest conviction institutional signal badge.
 */
export const computeStockDealBadge = (deals: BulkBlockDeal[]): StockDealBadgeInfo | null => {
  if (!deals || deals.length === 0) return null;

  // Sort by date desc, then valueCr desc
  const sorted = [...deals].sort((a, b) => {
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    return b.valueCr - a.valueCr;
  });

  // 1. Super / Marquee Investor deal (highest priority signal)
  const superDeal = sorted.find((d) => d.category === 'Marquee Investor');
  if (superDeal) {
    const action = superDeal.side === 'B' ? 'Bought' : 'Sold';
    return {
      type: 'super_investor',
      icon: '⭐',
      label: `Super Inv ${action}`,
      shortLabel: 'Super Inv',
      badgeClass: 'stock-deal-badge--super',
      title: `Super Investor ${action}: ${superDeal.clientName} transacted ₹${superDeal.valueCr} Cr on ${superDeal.date}`,
      dealDate: superDeal.date,
      valueCr: superDeal.valueCr,
    };
  }

  // 2. Mega Block Window (>= ₹25 Cr)
  const megaBlock = sorted.find((d) => d.dealType === 'block' && d.valueCr >= 25);
  if (megaBlock) {
    return {
      type: 'mega_block',
      icon: '🎯',
      label: `Block ₹${Math.round(megaBlock.valueCr)}Cr`,
      shortLabel: 'Mega Block',
      badgeClass: 'stock-deal-badge--block',
      title: `Mega Block: ${megaBlock.clientName} (${megaBlock.side === 'B' ? 'Buy' : 'Sell'} ₹${megaBlock.valueCr} Cr on ${megaBlock.date})`,
      dealDate: megaBlock.date,
      valueCr: megaBlock.valueCr,
    };
  }

  // 3. FII / DII Institutional Big Money (Buy >= ₹10 Cr)
  const institutionalWhale = sorted.find(
    (d) =>
      d.side === 'B' &&
      (d.category === 'Mutual Fund / DII' || d.category === 'FII / Global') &&
      d.valueCr >= 10
  );
  if (institutionalWhale) {
    return {
      type: 'fii_dii_mega',
      icon: '🏦',
      label: `FII/DII Buy ₹${Math.round(institutionalWhale.valueCr)}Cr`,
      shortLabel: 'Inst Buy',
      badgeClass: 'stock-deal-badge--whale',
      title: `Institutional Accumulation: ${institutionalWhale.clientName} (Bought ₹${institutionalWhale.valueCr} Cr on ${institutionalWhale.date})`,
      dealDate: institutionalWhale.date,
      valueCr: institutionalWhale.valueCr,
    };
  }

  // 4. Promoter / Corporate strategic shift (>= ₹10 Cr)
  const promoterDeal = sorted.find(
    (d) =>
      (d.category === 'Promoter / Group' || d.category === 'Corporate / Other') &&
      d.valueCr >= 10
  );
  if (promoterDeal) {
    const isPromoter = promoterDeal.category === 'Promoter / Group';
    return {
      type: 'promoter',
      icon: isPromoter ? '👑' : '🏢',
      label: isPromoter
        ? `Promoter ${promoterDeal.side === 'B' ? 'Buy' : 'Sell'}`
        : `Corp ${promoterDeal.side === 'B' ? 'Buy' : 'Sell'}`,
      shortLabel: isPromoter ? 'Promoter' : 'Corp Shift',
      badgeClass: 'stock-deal-badge--promoter',
      title: `${isPromoter ? 'Promoter Stake Action' : 'Corporate Shift'}: ${promoterDeal.clientName} (${promoterDeal.side === 'B' ? 'Bought' : 'Sold'} ₹${promoterDeal.valueCr} Cr on ${promoterDeal.date})`,
      dealDate: promoterDeal.date,
      valueCr: promoterDeal.valueCr,
    };
  }

  // 5. Significant Bulk Deal (>= ₹5 Cr)
  const notableDeal = sorted.find((d) => d.valueCr >= 5);
  if (notableDeal) {
    const isBuy = notableDeal.side === 'B';
    return {
      type: 'bulk_deal',
      icon: '🤝',
      label: `${notableDeal.dealType === 'block' ? 'Block' : 'Bulk'} ₹${Math.round(notableDeal.valueCr)}Cr`,
      shortLabel: 'Big Deal',
      badgeClass: isBuy ? 'stock-deal-badge--buy' : 'stock-deal-badge--sell',
      title: `${notableDeal.dealType.toUpperCase()} Deal: ${notableDeal.clientName} (${isBuy ? 'Bought' : 'Sold'} ₹${notableDeal.valueCr} Cr on ${notableDeal.date})`,
      dealDate: notableDeal.date,
      valueCr: notableDeal.valueCr,
    };
  }

  return null;
};

